// Single source of truth for the primary site navigation shown in the header
// (desktop) and the mobile hamburger menu across all pages. Keeping this list in
// one place is what stops the per-page menus from drifting apart.
//
// Order is the approved product order: Home, Templates, Pricing, Projects,
// Referrals. `requiresAuth` links are only shown to signed-in users.
//
// Community is deliberately absent: /community still exists and works, but it
// is no longer advertised anywhere in the site chrome. The "community" NavKey
// is kept so pages that already declare themselves active on it still type-
// check — re-adding a link here is all it takes to bring the menu entry back.
//
// The Discord invite is deliberately absent too: it is reachable from the
// workspace logo menu (below Help) only, not from the site nav. See
// lib/community-links.ts.

export type NavKey =
  | "home"
  | "templates"
  | "pricing"
  | "projects"
  | "community"
  | "referrals"
  | "profile";

export interface NavLink {
  href: string;
  label: string;
  key: NavKey;
  requiresAuth: boolean;
  /**
   * Rendered as unavailable, and never as a link — the destination is not ready
   * yet. Nothing in NAV_LINKS sets this today; `navLinksByKeys` filters on it so
   * a future inert entry cannot leak into a curated bar.
   */
  disabled?: boolean;
}

export const NAV_LINKS: NavLink[] = [
  { href: "/", label: "Home", key: "home", requiresAuth: false },
  { href: "/templates", label: "Templates", key: "templates", requiresAuth: false },
  { href: "/pricing", label: "Pricing", key: "pricing", requiresAuth: false },
  { href: "/projects", label: "Projects", key: "projects", requiresAuth: true },
  { href: "/referrals", label: "Referrals", key: "referrals", requiresAuth: true },
];

// Links visible to a viewer given their auth state. Auth-gated links (Projects,
// Referrals) are hidden from signed-out visitors.
export function visibleNavLinks(signedIn: boolean): NavLink[] {
  return NAV_LINKS.filter((link) => !link.requiresAuth || signedIn);
}

/**
 * The subset of NAV_LINKS a surface chooses to show, in the order the keys are
 * given, with auth-gated entries dropped for signed-out viewers.
 *
 * The homepage deliberately renders a much shorter bar than the full menu, but
 * it must not hand-roll its own hrefs and labels to do that. That drift is
 * exactly what left Referrals reachable from every surface EXCEPT the desktop
 * homepage — the one place logged-in users actually land (GT-04 / PANDA-1760).
 * A surface picks the keys; this list stays the source of truth for where each
 * one points and what it is called.
 *
 * Unknown keys are skipped rather than throwing — a typo should not blank the
 * navigation. `disabled` entries are dropped too: a curated bar has no room for
 * an inert "coming soon" pill, which is a full-menu affordance.
 */
export function navLinksByKeys(
  keys: readonly NavKey[],
  signedIn: boolean,
): NavLink[] {
  return keys
    .map((key) => NAV_LINKS.find((link) => link.key === key))
    .filter(
      (link): link is NavLink =>
        !!link && !link.disabled && (!link.requiresAuth || signedIn),
    );
}
